import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Printer, FileDown, Table2, FileBarChart, Mail, Loader2, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/lib/modules";
import { printDoc, pmoney } from "@/lib/print";
import { downloadCustomReportPdf, downloadCustomReportCsv } from "@/lib/customReportExport";
import DateRangeQuickPicker from "@/components/reports/DateRangeQuickPicker";
import EmailReportDialog from "@/components/reports/EmailReportDialog";

// R1-04B: Custom Report Builder. Lets the Owner/Manager combine any mix of the EXISTING report
// sections (same figures already shown on the four report tabs) into one branded document for a
// chosen period. No new calculation lives here — GET /reports/custom (+/pdf, +/csv) on the backend
// just reuses reports(), the /reports/products aggregation, ar_aging() and the prepaid/purchasing
// routers' own report_fn. This component only presents that same data differently.
const ALL_SECTIONS = [
  { key: "business", label: "Business Sales & Profit/Loss" },
  { key: "by_cashier", label: "Sales by Cashier" },
  { key: "by_method", label: "Sales by Payment Method" },
  { key: "products", label: "All Products Sold" },
  { key: "expenses", label: "Expenses by Category" },
  { key: "refunds", label: "Refunds & Returns" },
];
const MODULE_SECTIONS = [
  { key: "wallet", label: "NEEC MONEY Activity", gate: "prepaid_accounts" },
  { key: "purchasing", label: "Purchasing & Supplier Activity", gate: "suppliers" },
];
const SNAPSHOT_SECTIONS = [
  { key: "inventory", label: "Inventory" },
  { key: "ar", label: "Accounts Receivable" },
];
const todayYMD = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const firstOfMonthYMD = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };

export default function CustomReportBuilder({ open, onOpenChange, defaultRange }) {
  const { business, user } = useAuth();
  const modules = useModules();
  const canEmail = user?.role === "owner"; // R1-05: Owner-only for V1, no new Manager permission introduced.
  const cur = business?.currency || "JMD $";
  const [range, setRange] = useState(defaultRange || { start: firstOfMonthYMD(), end: todayYMD() });
  const [sections, setSections] = useState(["business"]);
  const [groupBy, setGroupBy] = useState("sku");
  const [busy, setBusy] = useState(""); // "", "print", "pdf", "csv"
  const [showEmail, setShowEmail] = useState(false);

  // Re-sync to the Reports page's current period each time the dialog is (re)opened, so the
  // builder starts from whatever range the Owner was already looking at instead of always
  // resetting to "this month".
  useEffect(() => { if (open && defaultRange) setRange(defaultRange); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key) => setSections((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  const availableModuleSections = MODULE_SECTIONS.filter((s) => modules.isAvailable(s.gate));
  const sectionLabel = (key) => [...ALL_SECTIONS, ...MODULE_SECTIONS, ...SNAPSHOT_SECTIONS].find((s) => s.key === key)?.label || key;
  // R1-05: the exact label server.py's `_CUSTOM_REPORT_SECTIONS` uses to build the real
  // attachment filename — only differs from `sectionLabel()` for inventory/ar (the UI's
  // "Current Snapshots" header already supplies that context, so the checkbox/summary labels
  // above stay short; the email preview still needs the backend's full wording to match the
  // real attached file name exactly).
  const emailReportLabel = (keys) => (keys.length === 1
    ? { inventory: "Inventory (Current Snapshot)", ar: "Accounts Receivable (Current Snapshot)" }[keys[0]] || sectionLabel(keys[0])
    : "Custom Report");

  const commonParams = () => ({ start: range.start, end: range.end, sections: sections.join(","), group_by: groupBy });
  // Real top-level navigation (not a scripted window.open) — opens as a fully standalone page,
  // not dependent on this dialog's timing or any embedding/sandbox restrictions around it.
  const newTabUrl = () => `/reports/custom/print-view?${new URLSearchParams(commonParams()).toString()}`;

  const doPrint = () => {
    if (!sections.length) return toast.error("Select at least one report section");
    // R1-04B fix (2026-10-29): open the print window SYNCHRONOUSLY, in direct response to this
    // click, *before* the async fetch below — see the `win` comment in lib/print.js. Opening it
    // only after the `await` broke the real Windows/browser Print dialog: the window still showed
    // the report, but window.print() inside it was silently dropped by the browser because the
    // window was no longer created within the click's trusted user-activation window.
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) { toast.error("Please allow pop-ups to print this report"); return; }
    w.document.write('<!doctype html><html><body style="font-family:sans-serif;padding:60px;color:#64748b">Preparing your report…</body></html>');
    (async () => {
      setBusy("print");
      try {
        const { data } = await api.get("/reports/custom", { params: commonParams() });
        printDoc({ business, docTitle: "Custom Report", docRef: "", meta: [["Period", `${range.start} → ${range.end}`]], body: buildCustomReportHtml(data, cur), accent: "#0ea5e9", plain: true, pageNumbers: true, footerNote: `Custom Report  ·  Period: ${range.start} → ${range.end}  ·  Generated ${new Date().toLocaleString()}`, win: w, printButton: true });
      } catch {
        w.close();
        toast.error("Could not build the report — please try again");
      }
      setBusy("");
    })();
  };

  const doPdf = async () => {
    if (!sections.length) return toast.error("Select at least one report section");
    setBusy("pdf");
    const err = await downloadCustomReportPdf(commonParams(), range);
    if (err) toast.error(err);
    setBusy("");
  };

  const doCsv = async () => {
    if (!sections.length) return toast.error("Select at least one report section");
    setBusy("csv");
    const err = await downloadCustomReportCsv(commonParams(), range);
    if (err) toast.error(err);
    setBusy("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="custom-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileBarChart className="w-5 h-5 text-sky-500" />Build Custom Report</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label className="text-xs mb-1.5 block">Period</Label>
            <DateRangeQuickPicker value={range} onChange={(start, end) => setRange({ start, end })} testidPrefix="crb-quick" startTestId="crb-start" endTestId="crb-end" />
          </div>

          <div>
            <Label className="text-xs mb-2 block">Sections</Label>
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
              {ALL_SECTIONS.map((s) => (
                <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`crb-section-${s.key}`}>
                  <Checkbox checked={sections.includes(s.key)} onCheckedChange={() => toggle(s.key)} data-testid={`crb-checkbox-${s.key}`} />
                  {s.label}
                </label>
              ))}
            </div>
            {sections.includes("products") && (
              <div className="flex items-center gap-2 mt-2 ml-6 text-xs text-muted-foreground" data-testid="crb-products-groupby">
                <span>Products shown as:</span>
                <Button type="button" size="sm" variant={groupBy === "sku" ? "default" : "outline"} className="h-6 px-2 text-xs" onClick={() => setGroupBy("sku")} data-testid="crb-groupby-sku">SKU / Variant</Button>
                <Button type="button" size="sm" variant={groupBy === "parent" ? "default" : "outline"} className="h-6 px-2 text-xs" onClick={() => setGroupBy("parent")} data-testid="crb-groupby-parent">Parent Rollup</Button>
              </div>
            )}
          </div>

          {availableModuleSections.length > 0 && (
            <div>
              <Label className="text-xs mb-2 block">Enabled Modules</Label>
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
                {availableModuleSections.map((s) => (
                  <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`crb-section-${s.key}`}>
                    <Checkbox checked={sections.includes(s.key)} onCheckedChange={() => toggle(s.key)} data-testid={`crb-checkbox-${s.key}`} />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs mb-2 block">Current Snapshots <span className="font-normal text-muted-foreground">(as of today, not limited to the period above)</span></Label>
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
              {SNAPSHOT_SECTIONS.map((s) => (
                <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`crb-section-${s.key}`}>
                  <Checkbox checked={sections.includes(s.key)} onCheckedChange={() => toggle(s.key)} data-testid={`crb-checkbox-${s.key}`} />
                  {s.label}
                </label>
              ))}
            </div>
          </div>

          {sections.length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="crb-selected-summary">
              {sections.length} section{sections.length > 1 ? "s" : ""} selected: {sections.map(sectionLabel).join(", ")}
            </p>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <p className="text-xs text-muted-foreground self-center">Owner &amp; Manager only · Cashiers have no Reports access</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" asChild disabled={!sections.length}>
              <a href={sections.length ? newTabUrl() : undefined} target="_blank" rel="noopener noreferrer" data-testid="crb-newtab-btn" onClick={(e) => { if (!sections.length) { e.preventDefault(); toast.error("Select at least one report section"); } }}>
                <ExternalLink className="w-4 h-4 mr-1.5" />Open in New Tab
              </a>
            </Button>
            <Button variant="outline" onClick={doPrint} disabled={!!busy} data-testid="crb-print-btn">{busy === "print" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Printer className="w-4 h-4 mr-1.5" />}Print</Button>
            <Button variant="outline" onClick={doPdf} disabled={!!busy} data-testid="crb-pdf-btn"><FileDown className="w-4 h-4 mr-1.5" />Download PDF</Button>
            <Button onClick={doCsv} disabled={!!busy} data-testid="crb-csv-btn">{busy === "csv" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Table2 className="w-4 h-4 mr-1.5" />}Download CSV</Button>
            {canEmail && <Button variant="outline" onClick={() => { if (!sections.length) return toast.error("Select at least one report section"); setShowEmail(true); }} disabled={!!busy} data-testid="crb-email-btn"><Mail className="w-4 h-4 mr-1.5" />Email Report</Button>}
          </div>
        </DialogFooter>
      </DialogContent>
      <EmailReportDialog open={showEmail} onOpenChange={setShowEmail} sections={sections} range={range} groupBy={groupBy} reportLabel={emailReportLabel(sections)} />
    </Dialog>
  );
}

// Builds the same combined-sections body HTML that the backend PDF builder renders, for the
// browser Print path — reusing print.js's existing table/section conventions and pmoney() helper
// exactly as Reports.js's own printReport() already does (R1-04A). Exported so the standalone
// "Open in New Tab" print view (CustomReportPrintView.jsx) renders the identical content.
export function buildCustomReportHtml(data, cur) {
  const m = (v) => pmoney(v, cur);
  const signed = (v) => (v < 0 ? `-${m(Math.abs(v))}` : m(v));
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const sec = data.sections;
  const kv = (rows) => `<table class="data"><tbody>${rows.map((r) => `<tr><td>${esc(r[0])}</td><td class="right"><strong>${esc(r[1])}</strong></td></tr>`).join("")}</tbody></table>`;
  const list = (title, arr, nameCol, caption) => `<div class="section"><h3>${esc(title)}</h3>${caption ? `<p class="notes">${esc(caption)}</p>` : ""}<table class="data"><thead><tr><th>${esc(nameCol)}</th><th class="right">Amount</th></tr></thead><tbody>${arr.map((x) => `<tr><td>${esc(x.name)}</td><td class="right">${m(x.total ?? x.revenue)}</td></tr>`).join("") || "<tr><td colspan=\"2\">No data for this period.</td></tr>"}</tbody></table></div>`;
  const rowsTable = (title, headers, rows, cells, note, caption) => `<div class="section"><h3>${esc(title)}</h3>${caption ? `<p class="notes">${esc(caption)}</p>` : ""}${rows.length ? `<table class="data"><thead><tr>${headers.map((h) => `<th${h.right ? ' class="right"' : ""}>${esc(h.label)}</th>`).join("")}</tr></thead><tbody>${rows.map((r) => `<tr>${cells(r).map((c, i) => `<td${headers[i].right ? ' class="right"' : ""}>${esc(c)}</td>`).join("")}</tr>`).join("")}</tbody></table>` : `<p class="notes">${esc(note || "No data for this period.")}</p>`}</div>`;

  let html = "";
  if (sec.business) {
    const b = sec.business;
    html += `<div class="section"><h3>Business Sales &amp; Profit/Loss</h3>${kv([["Total Sales", m(b.sales_total)], ["Gross Profit (estimate)", m(b.gross_profit)], ["Expenses", m(b.expenses_total)], ["Operating Profit/Loss", signed(b.operating_profit)], ["Payments Collected", m(b.payments_collected)]])}</div>`;
  }
  if (sec.by_cashier) {
    const unmatched = sec.by_cashier_unmatched || [];
    let cap = "Net of refunds processed by each cashier in this period.";
    if (unmatched.length) cap += ` No matching staff account found for: ${unmatched.join(", ")} — likely seeded/imported data, not an active cashier.`;
    html += list("Sales by Cashier", sec.by_cashier, "Cashier", cap);
  }
  if (sec.by_method) html += list("Sales by Payment Method (Gross Tendered)", sec.by_method, "Method", "Gross amount tendered at time of sale — not reduced by refunds (see Refunds & Returns below for the amount returned by method).");
  if (sec.expenses) html += list("Expenses by Category", sec.expenses, "Category");
  if (sec.refunds) {
    html += list("Refunds & Returns — by Payment Method", sec.refunds.by_method, "Method");
    html += rowsTable("Refunds & Returns — Items", [{ label: "Date" }, { label: "Type" }, { label: "Ref #" }, { label: "Original Invoice" }, { label: "Cashier" }, { label: "Amount", right: true }],
      sec.refunds.items, (r) => [r.date, r.type, r.refund_number || "-", r.original_invoice_number || "-", r.cashier || "-", m(r.amount)]);
    if (sec.refunds.truncated) html += `<p class="notes" style="color:#be123c">List capped at the oldest items in range — some refunds for this period are not shown.</p>`;
  }
  if (sec.products) {
    const p = sec.products;
    const cap = `Grand total — net qty ${p.totals.net_qty.toLocaleString()}, net revenue ${m(p.totals.net_revenue)}. Net revenue is line-item price × quantity only — it excludes invoice-level and per-item discounts, tax and delivery charges (see Business Sales & Profit/Loss above for full invoice totals).`;
    html += rowsTable("All Products Sold", [{ label: "Product" }, { label: "SKU" }, { label: "Variant" }, { label: "Qty Sold", right: true }, { label: "Returned", right: true }, { label: "Net Qty", right: true }, { label: "Net Revenue", right: true }],
      p.rows, (r) => [r.name, r.sku || "-", r.variant || "-", r.qty_sold, r.qty_returned, r.net_qty, m(r.net_revenue)], null, cap);
    if (p.truncated) html += `<p class="notes" style="color:#be123c">List capped — some lower-revenue products for this period are not shown; the grand total above reflects only the listed rows.</p>`;
  }
  if (sec.wallet) {
    html += `<p class="notes">NEEC MONEY — Total Funds Held (current snapshot, not period-filtered): ${m(sec.wallet.total_held || 0)}</p>`;
    html += rowsTable("NEEC MONEY — By Customer (this period)", [{ label: "Customer" }, { label: "Deposited", right: true }, { label: "Spent", right: true }, { label: "Refunded", right: true }, { label: "Balance", right: true }],
      sec.wallet.customers || [], (r) => [r.name, m(r.deposited || 0), m(r.spent || 0), m(r.refunded || 0), m(r.balance || 0)]);
  }
  if (sec.purchasing) {
    const s = sec.purchasing.summary || {};
    html += `<p class="notes">Purchasing — Open PO value (current snapshot, not period-filtered): ${m((s.open_value || 0) + (s.partial_value || 0))} · Outstanding to receive: ${m(s.outstanding_value || 0)}</p>`;
    html += `<div class="section"><h3>Purchasing &amp; Supplier Activity (this period)</h3>${kv([["Receipts", m(s.gross_received_value || 0)], ["Corrections", m(s.corrected_value || 0)], ["Returns", m(s.returns_value || 0)]])}</div>`;
  }
  if (sec.inventory) {
    html += `<p class="notes">Inventory — current snapshot, not period-filtered. Total value: ${m(sec.inventory.total_value)}</p>`;
    html += rowsTable("Inventory", [{ label: "Product" }, { label: "SKU" }, { label: "Variant" }, { label: "Qty On Hand", right: true }, { label: "Unit Cost", right: true }, { label: "Value", right: true }],
      sec.inventory.rows, (r) => [r.name, r.sku || "-", r.variant || "-", r.quantity, m(r.unit_cost), m(r.value)]);
    if (sec.inventory.truncated) html += `<p class="notes" style="color:#be123c">List capped — some lower-value items are not shown; the total value above reflects only the listed rows.</p>`;
  }
  if (sec.ar) {
    html += `<p class="notes">Accounts Receivable — current snapshot, not period-filtered. Total owed: ${m(sec.ar.total_owed)} · Total overdue: ${m(sec.ar.total_overdue)}</p>`;
    html += rowsTable("Accounts Receivable", [{ label: "Customer" }, { label: "Balance", right: true }, { label: "Overdue", right: true }, { label: "Days Overdue", right: true }],
      sec.ar.all_owing, (r) => [r.name, m(r.balance), m(r.overdue), r.max_days_overdue || 0]);
  }
  (data.unavailable || []).forEach((u) => { html += `<p class="notes" style="color:#be123c">${esc(u.reason)} — section omitted.</p>`; });
  return html;
}
